import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { refreshCsrfToken, setCsrfToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=authed
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Always fetch a CSRF token first — the anon token protects the
      // subsequent /auth/me and any anon mutation the user may perform
      // (login / register / forgot).
      await refreshCsrfToken();
    } catch {
      // fall through; /auth/me may still work if a valid session cookie
      // is present, but mutating operations will fail closed.
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Rotate to an auth-scoped CSRF token now that we have a session.
      await refreshCsrfToken().catch(() => {});
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, otp = "") => {
    const { data } = await api.post("/auth/login", { email, password, otp });
    if (data.mfa_required) return data;
    setUser(data.user);
    await refreshCsrfToken();  // rotate anon -> auth token
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    await refreshCsrfToken();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
    setCsrfToken("");
    await refreshCsrfToken().catch(() => {});
  };

  const logoutAll = async () => {
    try {
      await api.post("/auth/logout-all");
    } catch {}
    setUser(false);
    setCsrfToken("");
    await refreshCsrfToken().catch(() => {});
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, logoutAll, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
